import React, { useEffect, useState } from "react";
import {
	Form,
	Button,
	Card,
	CardBody,
	CardTitle,
	CardHeader,
	Input,
	FormGroup,
	Label,
	Row,
} from "reactstrap";

import { Controller, useForm, FormProvider } from "react-hook-form";
import NumberFormat from "react-number-format";

import { object, string } from "yup";
import { yupResolver } from "@hookform/resolvers";
import { setLocale } from "yup";

import { saveAuctionCompany } from "api/VendorsAPI";
import { saveFailAlert, saveSuccessAlert } from "utils/alertUtils";
import { showApiError } from "utils/errorRoutingUtils";
import { useFromUser } from "stores/LocalStorageHelper";
import { stateOptions } from "constants/Constants";

import ChildrenModal from "components/modals/ChildrenModal";
import AuctionCompaniesTable from "./AuctionCompaniesTable";
import { PhoneMask } from "../../../forms/components/InputElement";

const initialFormState = {
	id: null,
	name: "",
	permitNumber: "",
	dealerNumber: "",
	phoneNumber: "",
	address1: "",
	address2: "",
	city: "",
	state: "",
	zip: "",
};

const AuctionCompanySettings = () => {
	const dealerID = useFromUser("dealerID");
	const locationID = useFromUser("locationID");

	const [open, setOpen] = useState(false);
	const toggle = () => setOpen(!open);
	const [refresh, setRefresh] = useState(false);
	const [updating, setUpdating] = useState(false);
	const [formValues, setFormValues] = useState(initialFormState);

	const methods = useForm({
		defaultValues: initialFormState,
		reValidateMode: "onBlur",
		resolver: yupResolver(
			object().shape({
				name: string().required("Name is required"),
			})
		),
	});

	const { reset, handleSubmit, control, setValue, errors, register } = methods;

	useEffect(() => {
		register({ name: "id" });
	}, [register]);

	const clearFields = () => {
		setFormValues(initialFormState);
		reset(initialFormState);
		setUpdating(false);
	};

	const addAuctionCompany = (formData) => {
		console.log("Form payload:", formData);
		const auctionCompany = {
			...formData,
			dealerID,
			locationID,
			isHidden: false,
		};

		saveAuctionCompany(auctionCompany).then(
			() => {
				saveSuccessAlert();
				setRefresh(!refresh);
				clearFields();
				toggle();
			},
			(err) => {
				if (!err.isGeneralError) {
					showApiError(err, saveFailAlert);
				}
			}
		);
	};

	const editFunctions = {
		setFields: (data) => {
			setFormValues(data);
			reset(data); // <- includes ID and all fields
			setUpdating(true);
		},
		setUpdating,
	};

	useEffect(() => {
		if (open) {
			reset(formValues);
		}
	}, [open, formValues, reset]);

	setLocale({ mixed: { required: "Required" } });

	const renderInput = (name, label, type = "text") => (
		<FormGroup className="col-sm-3">
			<Label for={name}>{label}</Label>
			<Controller
				name={name}
				control={control}
				render={({ value, onChange }) => (
					<Input
						id={name}
						name={name}
						value={value}
						type={type}
						onChange={(e) => onChange(e.target.value)}
						invalid={!!errors[name]}
					/>
				)}
			/>
		</FormGroup>
	);

	return (
		<div className="row mx-0 p-3">
			<FormProvider {...methods}>
				<ChildrenModal
					modal={open}
					toggle={toggle}
					children={
						<Form>
							<Row>
								{renderInput("name", "Name")}
								{renderInput("permitNumber", "Permit Number")}
								{renderInput("dealerNumber", "Dealer Number")}

								<FormGroup className="col-sm-3">
									<Label for="phoneNumber">Phone Number</Label>
									<Controller
										name="phoneNumber"
										control={control}
										render={({ value, onChange }) => (
											<NumberFormat
												id="phoneNumber"
												className="form-control"
												format={PhoneMask()} //"+1 (###) ###-####"
												allowEmptyFormatting={false}
												mask="_"
												isNumericString={true}
												value={value}
												onValueChange={({ value }) => onChange(value)}
											/>
										)}
									/>
								</FormGroup>

								{renderInput("address1", "Address Line 1")}
								{renderInput("address2", "Address Line 2")}
								{renderInput("city", "City")}

								<FormGroup className="col-sm-3">
									<Label for="state">State</Label>
									<Controller
										name="state"
										control={control}
										render={({ value, onChange }) => (
											<Input
												id="state"
												type="select"
												value={value}
												onChange={(e) => onChange(e.target.value)}
											>
												<option value="">Select a state</option>
												{stateOptions.map((opt) => (
													<option key={opt.value} value={opt.value}>
														{opt.label}
													</option>
												))}
											</Input>
										)}
									/>
								</FormGroup>

								<FormGroup className="col-sm-3">
									<Label for="zip">Zip Code</Label>
									<Controller
										name="zip"
										control={control}
										render={({ value, onChange }) => (
											<Input
												id="zip"
												value={value}
												onChange={(e) =>
													onChange(
														e.target.value.replace(/[^\d]/g, "").substr(0, 5)
													)
												}
											/>
										)}
									/>
								</FormGroup>

								<div className="w-100 text-center">
									<Button
										color="primary"
										onClick={handleSubmit(addAuctionCompany)}
									>
										<i className="nc-icon nc-simple-add" />{" "}
										{updating ? "Update" : "Add"}
									</Button>
								</div>
							</Row>
						</Form>
					}
				/>
			</FormProvider>

			<div className="col pb-5">
				<div className="row mx-0">
					<Button
						className="btn btn-primary btn-sm"
						onClick={() => {
							clearFields();
							toggle();
						}}
					>
						<i className="nc-icon nc-simple-add" /> Add Auction Company
					</Button>
				</div>

				<Card>
					<CardHeader>
						<CardTitle>
							<h3>Auction Companies</h3>
						</CardTitle>
					</CardHeader>
					<CardBody>
						<AuctionCompaniesTable
							refresh={refresh}
							editFields={editFunctions}
							setRefresh={setRefresh}
							toggle={toggle}
						/>
					</CardBody>
				</Card>
			</div>
		</div>
	);
};

export default AuctionCompanySettings;
